import type { Client, Sale } from "@/types";
import { formatCOP, formatNumber } from "@/utils/format";

interface ClientTableProps {
  clients: Client[];
  sales: Sale[];
  selectedIds: string[];
  onToggleSelect: (id: string) => void;
  onToggleAll: () => void;
  onView: (client: Client) => void;
  onEdit: (client: Client) => void;
  onDelete: (client: Client) => void;
}

// Caja de selección reutilizable (escritorio y celular).
const SelectBox = ({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) => (
  <button
    type="button"
    onClick={onChange}
    aria-label={label}
    aria-pressed={checked}
    className={`w-5 h-5 shrink-0 flex items-center justify-center rounded-[6px] border cursor-pointer transition-colors ${
      checked
        ? "border-primary-500 bg-primary-500 text-foreground-950"
        : "border-background-400/60 text-transparent hover:border-primary-400"
    }`}
  >
    <i className="ri-check-line text-xs" />
  </button>
);

// Lista de clientes: tabla en escritorio y tarjetas en celular, con selección
// múltiple para borrar varios de una sola tanda.
const ClientTable = ({
  clients,
  sales,
  selectedIds,
  onToggleSelect,
  onToggleAll,
  onView,
  onEdit,
  onDelete,
}: ClientTableProps) => {
  const selected = new Set(selectedIds);
  const allSelected = clients.length > 0 && clients.every((c) => selected.has(c.id));

  const statsFor = (c: Client) => {
    const own = sales.filter(
      (s) =>
        (c.id && s.clientId === c.id) ||
        (!!c.name && s.clientName.trim().toLowerCase() === c.name.trim().toLowerCase())
    );
    return { count: own.length, total: own.reduce((a, s) => a + s.total, 0) };
  };

  const locationOf = (c: Client) =>
    [c.neighborhood, c.city, c.department, c.address].filter(Boolean).join(" · ") || "Sin ubicación";

  if (clients.length === 0) {
    return (
      <div className="glass-panel rounded-lg px-5 py-12 text-center">
        <span className="mx-auto w-12 h-12 rounded-lg bg-background-200/50 text-accent-200 flex items-center justify-center mb-3">
          <i className="ri-user-search-line text-2xl" />
        </span>
        <p className="text-sm text-foreground-700 font-semibold">Aún no hay clientes</p>
        <p className="text-xs text-foreground-500 mt-1">
          Crea tu primer cliente o cárgalos desde el respaldo de Excel.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Escritorio */}
      <div className="hidden lg:block glass-panel rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-foreground-500 border-b border-accent-500/15">
                <th className="w-12 px-4 py-3">
                  <SelectBox
                    checked={allSelected}
                    onChange={onToggleAll}
                    label={allSelected ? "Deseleccionar todos" : "Seleccionar todos"}
                  />
                </th>
                <th className="text-left font-semibold px-4 py-3">Cliente</th>
                <th className="text-left font-semibold px-4 py-3">Contacto</th>
                <th className="text-left font-semibold px-4 py-3">Ubicación</th>
                <th className="text-center font-semibold px-4 py-3">Compras</th>
                <th className="text-right font-semibold px-4 py-3">Facturado</th>
                <th className="text-center font-semibold px-4 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => {
                const st = statsFor(c);
                const isSel = selected.has(c.id);
                return (
                  <tr
                    key={c.id}
                    className={`border-b border-background-300/20 transition-colors ${
                      isSel ? "bg-primary-500/10" : "hover:bg-background-200/30"
                    }`}
                  >
                    <td className="px-4 py-3">
                      <SelectBox
                        checked={isSel}
                        onChange={() => onToggleSelect(c.id)}
                        label={`Seleccionar ${c.name}`}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => onView(c)}
                        className="text-left cursor-pointer group"
                        title="Ver ficha del cliente"
                      >
                        <p className="font-semibold text-foreground-950 group-hover:text-accent-200 transition-colors">
                          {c.name}
                        </p>
                        <p className="text-[11px] text-foreground-500">
                          {c.cedula ? `CC ${c.cedula}` : "Sin cédula"}
                        </p>
                      </button>
                    </td>
                    <td className="px-4 py-3 text-foreground-700">{c.phone || "—"}</td>
                    <td className="px-4 py-3 text-foreground-700 max-w-[240px]">
                      <span className="block truncate" title={locationOf(c)}>
                        {locationOf(c)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-foreground-950 font-semibold">
                      {formatNumber(st.count)}
                    </td>
                    <td className="px-4 py-3 text-right text-foreground-950 font-semibold">
                      {formatCOP(st.total)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => onView(c)}
                          className="w-9 h-9 flex items-center justify-center rounded-md border border-secondary-500/40 text-secondary-200 hover:bg-secondary-500/10 cursor-pointer"
                          aria-label="Ver ficha"
                          title="Ver ficha"
                        >
                          <i className="ri-eye-line" />
                        </button>
                        <button
                          onClick={() => onEdit(c)}
                          className="w-9 h-9 flex items-center justify-center rounded-md border border-accent-500/30 text-accent-200 hover:bg-accent-500/10 cursor-pointer"
                          aria-label="Editar cliente"
                          title="Editar"
                        >
                          <i className="ri-edit-line" />
                        </button>
                        <button
                          onClick={() => onDelete(c)}
                          className="w-9 h-9 flex items-center justify-center rounded-md border border-primary-400/40 text-primary-200 hover:bg-primary-500/10 cursor-pointer"
                          aria-label="Eliminar cliente"
                          title="Eliminar"
                        >
                          <i className="ri-delete-bin-line" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Celular */}
      <div className="lg:hidden space-y-3">
        {clients.map((c) => {
          const st = statsFor(c);
          const isSel = selected.has(c.id);
          return (
            <div
              key={c.id}
              className={`glass-panel rounded-lg p-4 ${isSel ? "border border-primary-500/40" : ""}`}
            >
              <div className="flex items-start gap-3">
                <div className="pt-0.5">
                  <SelectBox
                    checked={isSel}
                    onChange={() => onToggleSelect(c.id)}
                    label={`Seleccionar ${c.name}`}
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <button
                        onClick={() => onView(c)}
                        className="text-left cursor-pointer max-w-full"
                        title="Ver ficha del cliente"
                      >
                        <p className="font-semibold text-foreground-950 truncate">{c.name}</p>
                      </button>
                      <p className="text-[11px] text-foreground-500">
                        {c.cedula ? `CC ${c.cedula}` : "Sin cédula"}
                        {c.phone ? ` · ${c.phone}` : ""}
                      </p>
                    </div>
                    <span className="shrink-0 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-secondary-500/20 text-secondary-100">
                      {formatNumber(st.count)} compras
                    </span>
                  </div>
                  <p className="text-[11px] text-foreground-500 mt-2">{locationOf(c)}</p>
                  <div className="mt-3 flex items-center justify-between gap-2">
                    <span className="text-xs text-foreground-600">
                      Facturado:{" "}
                      <span className="font-semibold text-foreground-950">{formatCOP(st.total)}</span>
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onView(c)}
                        className="w-9 h-9 flex items-center justify-center rounded-md border border-secondary-500/40 text-secondary-200 hover:bg-secondary-500/10 cursor-pointer"
                        aria-label="Ver ficha"
                      >
                        <i className="ri-eye-line" />
                      </button>
                      <button
                        onClick={() => onEdit(c)}
                        className="w-9 h-9 flex items-center justify-center rounded-md border border-accent-500/30 text-accent-200 hover:bg-accent-500/10 cursor-pointer"
                        aria-label="Editar cliente"
                      >
                        <i className="ri-edit-line" />
                      </button>
                      <button
                        onClick={() => onDelete(c)}
                        className="w-9 h-9 flex items-center justify-center rounded-md border border-primary-400/40 text-primary-200 hover:bg-primary-500/10 cursor-pointer"
                        aria-label="Eliminar cliente"
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default ClientTable;